import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';

const NTT_RED = '#E4002B';
const NTT_RED_DARK = '#B0001F';
const NTT_RED_LIGHT = '#FF3352';
const NTT_NAVY = '#001433';
const NTT_NAVY_MID = '#003087';
const NTT_GRAY_BG = '#F4F6F9';
const NTT_SIDEBAR_BG = '#001433';

export const nttCustomTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      primary: {
        main: NTT_RED,
        light: NTT_RED_LIGHT,
        dark: NTT_RED_DARK,
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: NTT_NAVY,
        light: NTT_NAVY_MID,
        dark: '#000D26',
        contrastText: '#FFFFFF',
      },
      error: {
        main: '#C62828',
      },
      warning: {
        main: '#E65100',
      },
      info: {
        main: NTT_NAVY_MID,
      },
      success: {
        main: '#2E7D32',
      },
      background: {
        default: NTT_GRAY_BG,
        paper: '#FFFFFF',
      },
      banner: {
        info: NTT_NAVY_MID,
        error: NTT_RED,
        text: '#FFFFFF',
        link: '#FFFFFF',
      },
      errorBackground: '#FFEBEE',
      warningBackground: '#FFF3E0',
      infoBackground: '#E8EAF6',
      navigation: {
        background: NTT_SIDEBAR_BG,
        indicator: NTT_RED,
        color: 'rgba(255,255,255,0.65)',
        selectedColor: '#FFFFFF',
        navItem: {
          hoverBackground: 'rgba(228,0,43,0.18)',
        },
        submenu: {
          background: '#000D26',
        },
      },
      text: {
        primary: '#1A1A2E',
        secondary: '#52556A',
      },
    },
  }),
  fontFamily: '"Inter", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
  defaultPageTheme: 'home',
  components: {
    BackstagePage: {
      styleOverrides: {
        root: {
          background: NTT_GRAY_BG,
        },
      },
    },
    BackstageHeader: {
      styleOverrides: {
        header: () => ({
          width: 'auto',
          background: `linear-gradient(135deg, ${NTT_NAVY} 0%, ${NTT_NAVY_MID} 55%, ${NTT_RED} 100%)`,
          boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
          borderBottom: 'none',
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 2px 12px rgba(0,20,50,0.08)',
          border: '1px solid rgba(0,20,50,0.06)',
          transition: 'box-shadow 0.2s ease, transform 0.15s ease',
          '&:hover': {
            boxShadow: '0 6px 24px rgba(0,20,50,0.13)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          fontWeight: 600,
          fontSize: '0.73rem',
          letterSpacing: '0.01em',
        },
        colorPrimary: {
          backgroundColor: `${NTT_RED}18`,
          color: NTT_RED_DARK,
          border: `1px solid ${NTT_RED}40`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 600,
          letterSpacing: '0.02em',
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${NTT_RED} 0%, ${NTT_RED_DARK} 100%)`,
          boxShadow: `0 2px 8px ${NTT_RED}50`,
          '&:hover': {
            background: `linear-gradient(135deg, ${NTT_RED_LIGHT} 0%, ${NTT_RED} 100%)`,
            boxShadow: `0 4px 16px ${NTT_RED}60`,
          },
        },
        outlinedPrimary: {
          borderColor: NTT_RED,
          '&:hover': {
            backgroundColor: `${NTT_RED}10`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        head: {
          backgroundColor: `${NTT_GRAY_BG}!important`,
        },
        root: {
          backgroundColor: '#FFFFFF!important',
          '&:nth-of-type(even)': {
            backgroundColor: '#FAFBFC!important',
          },
          '&:hover': {
            backgroundColor: `${NTT_RED}08!important`,
          },
        },
      },
    },
    BackstageHeaderTabs: {
      styleOverrides: {
        tabsWrapper: {
          backgroundColor: NTT_NAVY,
        },
        defaultTab: {
          textDecoration: 'none!important',
          color: 'rgba(255,255,255,0.65)',
          fontSize: '0.875rem',
          fontWeight: 500,
          '&:hover': {
            background: 'rgba(255,255,255,0.08)!important',
            color: '#FFFFFF!important',
          },
        },
      },
    },
    BackstageSelectInputBase: {
      styleOverrides: {
        input: {
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid rgba(0,20,50,0.15)',
        },
      },
    },
    BackstageEmptyState: {
      styleOverrides: {
        root: {
          backgroundColor: NTT_GRAY_BG,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: NTT_RED,
          height: '3px',
          borderRadius: '3px 3px 0 0',
        },
      },
    },
    BackstageTableHeader: {
      styleOverrides: {
        header: {
          backgroundColor: NTT_GRAY_BG,
          fontWeight: 700,
          fontSize: '0.78rem',
          letterSpacing: '0.06em',
          textTransform: 'uppercase' as const,
          color: '#52556A',
        },
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
          borderRadius: '8px',
          margin: '1px 8px',
          transition: 'background 0.15s ease',
        },
        highlightable: {
          '&:hover': {
            background: 'rgba(228,0,43,0.18)!important',
            color: '#FFFFFF!important',
            borderRadius: '8px',
          },
        },
        selected: {
          background: `linear-gradient(135deg, ${NTT_RED} 0%, ${NTT_RED_DARK} 100%)`,
          borderRadius: '8px',
          color: '#FFFFFF!important',
          boxShadow: `0 2px 12px ${NTT_RED}55`,
        },
        label: {
          fontWeight: 500,
          fontSize: '0.875rem',
          letterSpacing: '0.01em',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        elevation1: {
          boxShadow: '0 2px 12px rgba(0,20,50,0.08)',
        },
        elevation2: {
          boxShadow: '0 4px 20px rgba(0,20,50,0.1)',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: `${NTT_RED}20`,
        },
        barColorPrimary: {
          background: `linear-gradient(90deg, ${NTT_RED} 0%, ${NTT_RED_DARK} 100%)`,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: NTT_NAVY,
          borderRadius: '6px',
          fontSize: '0.78rem',
          fontWeight: 500,
          boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
        },
        arrow: {
          color: NTT_NAVY,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(0,20,50,0.06)',
          fontSize: '0.875rem',
        },
        head: {
          fontWeight: 700,
          color: '#52556A',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: NTT_RED,
          fontWeight: 500,
          '&:hover': {
            color: NTT_RED_DARK,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: NTT_RED,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: NTT_RED,
            borderWidth: '2px',
          },
        },
      },
    },
  },
  pageTheme: {
    home: genPageTheme({ colors: [NTT_NAVY, NTT_RED], shape: shapes.wave }),
    documentation: genPageTheme({ colors: [NTT_NAVY, NTT_NAVY_MID], shape: shapes.wave2 }),
    tool: genPageTheme({ colors: [NTT_NAVY_MID, NTT_NAVY], shape: shapes.wave }),
    service: genPageTheme({ colors: [NTT_RED, NTT_NAVY], shape: shapes.wave }),
    website: genPageTheme({ colors: [NTT_NAVY_MID, NTT_NAVY], shape: shapes.wave }),
    library: genPageTheme({ colors: [NTT_NAVY, NTT_RED], shape: shapes.round }),
    other: genPageTheme({ colors: [NTT_NAVY, NTT_NAVY_MID], shape: shapes.wave }),
    app: genPageTheme({ colors: [NTT_RED, NTT_NAVY], shape: shapes.wave }),
    apis: genPageTheme({ colors: [NTT_NAVY_MID, NTT_NAVY], shape: shapes.wave }),
  },
});
